import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import api from "../services/api";
import Navbar from "../components/Navbar";
import { motion, AnimatePresence } from "framer-motion";

const WishlistPage = () => {
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchFavorites();
  }, []);

  const fetchFavorites = async () => {
    try {
      setLoading(true);
      const res = await api.get("/favorites");
      setFavorites(res.data.products || []);
      
      // Sync with localStorage
      const favIds = (res.data.products || []).map(p => p._id);
      localStorage.setItem("favorites", JSON.stringify(favIds));
    } catch (error) {
      console.error("Lỗi khi tải danh sách yêu thích:", error);
      toast.error("Không thể tải danh sách yêu thích");
    } finally {
      setLoading(false);
    }
  };

  const removeFavorite = async (productId) => {
    try {
      await api.post("/favorites/toggle", { productId });
      setFavorites(prev => prev.filter(p => p._id !== productId));
      
      // Update localStorage
      const currentFavs = JSON.parse(localStorage.getItem("favorites") || "[]");
      const updatedFavs = currentFavs.filter(id => id !== productId);
      localStorage.setItem("favorites", JSON.stringify(updatedFavs));
      
      toast.success("Đã xóa khỏi danh sách yêu thích");
    } catch (error) {
      toast.error("Lỗi khi xóa sản phẩm");
    }
  };

  return (
    <div 
      className="min-h-screen bg-gray-50 dark:bg-slate-950 pt-16 sm:pt-32 pb-20 transition-colors duration-500"
      style={{ 
        zoom: "0.8",
        MozTransform: "scale(0.8)",
        MozTransformOrigin: "top center"
      }}
    >
      <Navbar />
      <div className="max-w-6xl mx-auto px-4 mt-8">
        <div className="mb-10 text-center">
          <h1 className="text-3xl sm:text-5xl font-black text-gray-900 dark:text-white mb-3">
            Sản phẩm yêu thích
          </h1>
          <p className="text-sm sm:text-base text-gray-500 dark:text-slate-400">
            Danh sách những đôi giày bạn đã "thả tim"
          </p>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-20">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mb-4"></div>
            <p className="text-gray-500 dark:text-slate-400 font-medium">Đang tải danh sách...</p>
          </div>
        ) : favorites.length === 0 ? (
          <div className="bg-white dark:bg-slate-900 rounded-[32px] p-12 sm:p-20 text-center shadow-xl border border-gray-100 dark:border-slate-800 animate-fade-in">
            <div className="w-24 h-24 bg-red-50 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <span className="material-symbols-outlined text-red-500 text-5xl">favorite</span>
            </div>
            <h3 className="text-2xl font-bold text-gray-800 dark:text-white mb-4">
              Chưa có sản phẩm nào
            </h3>
            <p className="text-gray-500 dark:text-slate-400 mb-10 max-w-md mx-auto leading-relaxed">
              Hãy dạo quanh cửa hàng và nhấn vào biểu tượng trái tim để lưu lại những đôi giày bạn yêu thích nhé!
            </p>
            <Link
              to="/products"
              className="inline-flex items-center gap-2 bg-blue-600 text-white px-10 py-4 rounded-2xl font-black hover:bg-blue-700 transition-all shadow-xl shadow-blue-100 dark:shadow-none active:scale-95"
            >
              <span className="material-symbols-outlined">shopping_bag</span>
              KHÁM PHÁ NGAY
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6 sm:gap-8">
            <AnimatePresence mode="popLayout">
              {favorites.map((product) => (
                <motion.div
                  layout
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  key={product._id}
                  className="group bg-white dark:bg-slate-900 rounded-[24px] overflow-hidden shadow-sm hover:shadow-2xl transition-all duration-500 border border-gray-100 dark:border-slate-800 relative"
                >
                  <Link to={`/product/${product._id}`} className="block">
                    <div className="aspect-[4/5] overflow-hidden bg-gray-50 dark:bg-slate-800 relative">
                      <img
                        src={product.image || product.images?.[0] || "https://placehold.co/400x500?text=No+Image"}
                        alt={product.name}
                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700"
                      />
                      <div className="absolute inset-0 bg-black/5 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                    <div className="p-4 sm:p-6">
                      <p className="text-[10px] font-black text-blue-600 dark:text-blue-400 uppercase tracking-widest mb-1">
                        {product.brand || "GoSporty"}
                      </p>
                      <h3 className="text-sm sm:text-base font-bold text-gray-800 dark:text-white line-clamp-2 min-h-[40px] mb-2 group-hover:text-blue-600 transition-colors">
                        {product.name}
                      </h3>
                      <div className="flex items-center justify-between mt-auto">
                        <span className="text-lg font-black text-gray-900 dark:text-white">
                          {new Intl.NumberFormat("vi-VN").format(product.price)}₫
                        </span>
                      </div>
                    </div>
                  </Link>
                  
                  {/* Remove Button */}
                  <button
                    onClick={() => removeFavorite(product._id)}
                    className="absolute top-4 right-4 w-10 h-10 bg-white/90 dark:bg-slate-800/90 backdrop-blur-md rounded-full flex items-center justify-center text-red-500 shadow-lg opacity-0 group-hover:opacity-100 transition-all hover:bg-red-500 hover:text-white active:scale-90"
                    title="Xóa khỏi yêu thích"
                  >
                    <span className="material-symbols-outlined text-[20px]">delete</span>
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}
      </div>
    </div>
  );
};

export default WishlistPage;
